import { Link } from 'react-router-dom';
import SEOHelmet from '@/components/base/SEOHelmet';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { faqs } from '@/mocks/faqs';

export default function AboutPage() {
  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '91安全指南',
    url: 'https://91-safety-guide.readdy.ai',
    description: '致力于提供网络安全资讯与使用指南的教育平台，帮助用户识别虚假页面、防范恶意下载和保护个人隐私。',
    foundingDate: '2025',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@91-safety-guide.readdy.ai',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <SEOHelmet
        title="关于我们"
        description="91安全指南致力于提供网络安全资讯与使用指南，帮助用户识别虚假页面、防范恶意下载和保护个人隐私。了解我们的品牌理念、信誉承诺与联系方式。"
        keywords="91视频下载,关于我们,品牌介绍,联系方式,网络安全平台"
        canonicalUrl="https://91-safety-guide.readdy.ai/about"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />

      {/* Header */}
      <section className="bg-background-100 border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="mb-4">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
            关于我们
          </h1>
        </div>
      </section>

      {/* Brand Introduction */}
      <section className="max-w-4xl mx-auto px-4 md:px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
              品牌介绍
            </h2>
            <div className="space-y-4 text-foreground-700 leading-relaxed">
              <p>
                <strong>91安全指南</strong>是一个独立的网络安全资讯平台，致力于为用户提供准确、实用的安全资讯和使用指南。
              </p>
              <p>
                我们深知，在互联网上搜索特定内容时，用户往往面临着虚假页面、恶意软件和隐私泄露等多重风险。因此，我们汇集了来自安全领域的专业知识和实践经验，为用户打造了这一个值得信赖的信息参考平台。
              </p>
              <p>
                本站<strong>不提供任何下载服务</strong>，也不隶属于任何第三方平台。我们的唯一使命是帮助用户识别风险、保护自己，在复杂的网络环境中安全前行。
              </p>
            </div>
          </div>
          <div className="relative h-64 md:h-72 rounded-xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20clean%20office%20environment%20with%20team%20collaborating%20on%20digital%20security%2C%20warm%20cream%20and%20amber%20tones%2C%20soft%20natural%20lighting%2C%20professional%20editorial%20style%2C%20calm%20atmosphere&width=800&height=600&seq=about-brand-image&orientation=landscape"
              alt="91安全指南团队 - 网络安全资讯平台"
              className="w-full h-full object-cover object-top"
              title="91安全指南团队"
            />
          </div>
        </div>
      </section>

      {/* Reputation / Credentials */}
      <section className="bg-background-100 py-14">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
            我们的信誉
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center">
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                <i className="ri-shield-check-line text-xl"></i>
              </div>
              <h3 className="font-heading font-semibold text-foreground-900 mb-2">安全可靠</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                所有内容均经过安全专业人士审核，确保信息的准确性和可靠性。我们不推送任何风险链接。
              </p>
            </div>
            <div className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center">
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                <i className="ri-eye-line text-xl"></i>
              </div>
              <h3 className="font-heading font-semibold text-foreground-900 mb-2">公开透明</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                我们清楚声明自身立场——独立的信息平台，不隶属于任何被讨论的第三方服务。所有信息来源均有明确标注。
              </p>
            </div>
            <div className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center">
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                <i className="ri-user-heart-line text-xl"></i>
              </div>
              <h3 className="font-heading font-semibold text-foreground-900 mb-2">用户至上</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                以用户安全为核心出发点，持续更新内容，回应读者关切。您的数字安全是我们最大的动力。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="max-w-2xl mx-auto px-4 md:px-6 py-14">
        <div className="text-center mb-8">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
            联系我们
          </h2>
          <p className="text-foreground-600">
            如有任何问题、建议或反馈，欢迎通过以下表单与我们取得联系
          </p>
        </div>
        <form
          data-readdy-form="true"
          action="https://readdy.ai/api/form/d9i8ik0hpes2mi34rv70"
          method="POST"
          className="space-y-5 bg-background-50 rounded-lg p-6 md:p-8 border border-background-200/70"
        >
          <div>
            <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
              您的姓名 <span className="text-primary-600">*</span>
            </label>
            <input
              type="text"
              id="contact-name"
              name="name"
              required
              className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-colors"
              placeholder="请输入您的姓名"
            />
          </div>
          <div>
            <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
              电子邮箱 <span className="text-primary-600">*</span>
            </label>
            <input
              type="email"
              id="contact-email"
              name="email"
              required
              className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-colors"
              placeholder="请输入您的电子邮箱"
            />
          </div>
          <div>
            <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
              主题
            </label>
            <input
              type="text"
              id="contact-subject"
              name="subject"
              className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-colors"
              placeholder="请输入消息主题"
            />
          </div>
          <div>
            <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
              消息内容 <span className="text-primary-600">*</span>
            </label>
            <textarea
              id="contact-message"
              name="message"
              required
              rows={5}
              maxLength={500}
              className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-colors resize-none"
              placeholder="请输入您的消息内容（不超过500字）"
            ></textarea>
            <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
          </div>
          <input type="text" name="phone_alt" autoComplete="off" tabIndex={-1} aria-hidden="true" readOnly className="hp-field" />
          <button
            type="submit"
            className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-md font-medium text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            发送消息
          </button>
        </form>
      </section>

      {/* Full FAQ */}
      <section className="bg-background-100 py-14">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
              完整常见问题
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq) => (
              <details
                key={faq.id}
                className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden"
              >
                <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                  <span className="font-medium text-foreground-900 pr-4">{faq.question}</span>
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:hidden">
                    <i className="ri-add-line"></i>
                  </div>
                  <div className="w-5 h-5 items-center justify-center flex-shrink-0 text-foreground-400 hidden group-open:flex">
                    <i className="ri-subtract-line"></i>
                  </div>
                </summary>
                <div className="px-5 pb-5 text-sm text-foreground-700 leading-relaxed">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="max-w-4xl mx-auto px-4 md:px-6 py-10">
        <div className="p-6 rounded-lg bg-secondary-50 border border-secondary-200/60">
          <h3 className="font-heading font-semibold text-foreground-900 mb-3 text-sm flex items-center gap-2">
            <i className="ri-information-line text-secondary-600"></i>
            免责声明
          </h3>
          <div className="text-sm text-foreground-600 leading-relaxed space-y-2">
            <p>本站（91安全指南）是一个独立的网络安全资讯平台，不隶属于任何第三方视频或下载服务。本站不提供任何形式的下载服务，不存储、不传播任何视频或文件内容。</p>
            <p>本站所有内容仅供安全学习和参考目的。用户在访问任何第三方网站或下载任何内容前，应当自行核实来源的可靠性和合法性，并遵守所在地区的法律法规。</p>
            <p>对于用户因参考本站内容而产生的任何直接或间接后果，本站不承担任何法律责任。</p>
          </div>
        </div>
      </section>
    </>
  );
}